'use client';

import type { ReactNode } from 'react';
import MaterialIcons from '@/components/MaterialIcons/materialIcons';
import { BookIcon } from '@/components/Icons/Icons';

interface LearningPathStats {
  courses: number;
  videos: number;
  quizzes: number;
  assessments: number;
}

interface LearningPathStatsProps {
  stats: LearningPathStats;
  className?: string;
  variant?: 'home' | 'profile';
  duration?: string;
}

export function LearningPathStats({ stats, className = '', variant = 'profile', duration }: LearningPathStatsProps) {
  const isHome = variant === 'home';
  const items = (
    [
      { key: 'courses' as const, count: stats.courses, singular: 'Course', plural: 'Courses' },
      { key: 'videos' as const, count: stats.videos, singular: 'Video', plural: 'Videos' },
      { key: 'quizzes' as const, count: stats.quizzes, singular: 'Quiz', plural: 'Quizzes' },
      { key: 'assessments' as const, count: stats.assessments, singular: 'Assessment', plural: 'Assessments' },
    ] as const
  ).filter((item) => item.count > 0);

  if (items.length === 0) return null;

  // For home: show on tablet and desktop (text format)
  // For profile: show on tablet only (text format), hidden on desktop
  const tabletTextClass = isHome 
    ? `hidden tab:flex items-center gap-4 flex-nowrap ${className}`
    : `hidden tab:flex xl:hidden items-center gap-4 flex-nowrap ${className}`;
  
  // Mobile: Icons with numbers – full-width container, items grouped at start (no spread)
  const mobileIconsClass = `flex tab:hidden items-center justify-start gap-4 flex-nowrap w-full min-w-0 ${className}`;

  const icons: Record<keyof LearningPathStats, ReactNode> = {
    courses: <BookIcon className="text-icon-light !w-5 !h-5 flex-shrink-0" />,
    videos: <MaterialIcons iconName="play_circle" className="text-icon-light !text-[20px] !leading-none flex-shrink-0" />,
    quizzes: <MaterialIcons iconName="quiz" className="text-icon-light !text-[20px] !leading-none flex-shrink-0" />,
    assessments: <MaterialIcons iconName="assignment" className="text-icon-light !text-[20px] !leading-none flex-shrink-0" />,
  };

  return (
    <>
      {/* Tablet/Desktop: Text with separators */}
      <div className={tabletTextClass}>
        {items.map((item, i) => (
          <span key={item.key} className="flex items-center gap-2">
            {i > 0 && <span className="h-8 w-px bg-border-medium" />}
            <span className="cs-caption flex items-center gap-2">
              <span className="text-text-default-medium">{item.count}</span>{' '}
              {item.count === 1 ? item.singular : item.plural}
            </span>
          </span>
        ))}
        {duration && (
          <span className="flex items-center gap-2">
            {items.length > 0 && <span className="h-8 w-px bg-border-medium" />}
            <span className="cs-caption">{duration}</span>
          </span>
        )}
      </div>

      {/* Mobile: Icons with numbers – inner w-fit keeps items grouped when container is full width */}
      <div className={mobileIconsClass}>
        <div className="flex items-center gap-4 flex-nowrap flex-shrink-0">
        {items.map((item, i) => (
          <span key={item.key} className="flex items-center gap-2 flex-shrink-0">
            {i > 0 && <span className="h-8 w-px bg-border-medium flex-shrink-0" />}
            <div className="flex items-center justify-center gap-2">
              <span className="flex items-center justify-center">{icons[item.key]}</span>
              <span className="cs-caption text-icon-medium leading-none flex items-center justify-center">{item.count}</span>
            </div>
          </span>
        ))}
        {duration && (
          <span className="flex items-center gap-2 flex-shrink-0">
            {items.length > 0 && <span className="h-8 w-px bg-border-medium flex-shrink-0" />}
            <span className="cs-caption text-icon-medium leading-none">{duration}</span>
          </span>
        )}
        </div>
      </div>
    </>
  );
}
